import React, { useState, useEffect, useMemo } from 'react';
import {
    ArrowLeft,
    CheckCheck,
    Inbox,
    Loader2,
    ChevronDown,
    Mail,
    MessageSquare,
    MessagesSquare,
    Phone,
    Pin,
    Sparkles,
    Star,
    Trash2,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import ConfirmDialog from '../../components/ConfirmDialog';
import API from '../../utils/api';
import useChatWindow from '../../hooks/useChatWindow';
import ChatComposer from '../../components/chat/ChatComposer';
import ChatAttachment from '../../components/chat/ChatAttachment';
import { onAdminRefresh } from '../../utils/adminSocket';
import { DeletedText, ForwardedTag, MessageFlags, PinnedBanner, Reactions, ReplyQuote } from '../../components/chat/MessageParts';
import useMessageMenu from '../../components/chat/useMessageMenu';
import { ForwardDialog, SavedMessagesDialog } from '../../components/chat/ChatDialogs';
import toast from 'react-hot-toast';
import './Messages.css';


const SERVER_URL = (API.defaults?.baseURL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const ATTACHMENT_LABEL = { image: 'Photo', video: 'Video', audio: 'Audio', document: 'File' };
const previewOf = (text, attachment) => (text && text.trim()) || (attachment?.url ? ATTACHMENT_LABEL[attachment.kind] || 'File' : '');
const uploadChatFile = async (file, onProgress) => {
    const data = new FormData();
    data.append('file', file);
    const res = await API.post('/chat-uploads', data, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: e => onProgress?.(Math.min(100, Math.round((e.loaded / (e.total || file.size || 1)) * 100))) });
    return res.data.attachment;
};

const FILTERS = [
    { id: 'all', label: 'All', Icon: Inbox },
    { id: 'unread', label: 'Unread', Icon: Mail },
    { id: 'replied', label: 'Replied', Icon: CheckCheck },
];

// one conversation per sender: by email, or by account when the person registered without an email
const threadKey = m => String(m.email || '').trim().toLowerCase() || (m.user ? `user:${m.user}` : '');
const initial = name => (name ? name.charAt(0).toUpperCase() : '?');
const senderIdentity = thread => {
    const account = thread?.senderAccount;
    if (account?.role === 'service') {
        return { kind: 'provider', label: 'Service Provider', publicId: account.providerId };
    }
    if (account?.role === 'member') {
        return { kind: 'member', label: 'Member', publicId: account.memberId };
    }
    return { kind: 'visitor', label: 'Visitor', publicId: null };
};
const formatTime = date => new Date(date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
const formatDate = date => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-IN');
};

// One contact message → chat bubbles. `mine` = written by the admin (right side).
const toBubbles = m => {
    if (m.fromAdmin) return [{ id: m._id, text: m.message, at: m.createdAt, mine: true, attachment: m.attachment }];
    const bubbles = [{ id: `${m._id}-q`, text: m.message, at: m.createdAt, mine: false, subject: m.subject, attachment: m.attachment }];
    if (m.reply) bubbles.push({ id: `${m._id}-a`, text: m.reply, at: m.repliedAt || m.createdAt, mine: true });
    return bubbles;
};

const Messages = () => {
    const [messages, setMessages] = useState([]);
    const [activeKey, setActiveKey] = useState(null);
    const [filter, setFilter] = useState('all');
    const [pendingDelete, setPendingDelete] = useState(null);

    const fetchMessages = async () => {
        try {
            const res = await API.get('/admin/messages');
            setMessages(res.data.messages);
        } catch (err) {
            toast.error('Failed to load messages');
        }
    };

    useEffect(() => {
        fetchMessages();
        const offRefresh = onAdminRefresh(fetchMessages); // new messages appear the moment they arrive
        const timer = setInterval(fetchMessages, 10000); // pick up new contact messages without a refresh
        return () => { clearInterval(timer); offRefresh(); };
    }, []);

    // Group the flat list into one conversation per sender (by email).
    const threads = useMemo(() => {
        const map = new Map();
        [...messages].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).forEach(m => {
            const key = threadKey(m);
            if (!map.has(key)) map.set(key, { key, thread: key, name: m.name, email: m.email, mobile: m.mobile, senderAccount: m.senderAccount, docs: [] });
            const t = map.get(key);
            t.docs.push(m);
            if (m.senderAccount) t.senderAccount = m.senderAccount;
            if (!m.fromAdmin) { t.name = m.name; t.mobile = m.mobile || t.mobile; }
        });
        return Array.from(map.values()).map(t => {
            const bubbles = t.docs.flatMap(toBubbles);
            const last = bubbles[bubbles.length - 1];
            return {
                ...t,
                bubbles,
                last,
                version: `${bubbles.length}:${t.docs.reduce((latest, d) => Math.max(latest, d.actionAt ? new Date(d.actionAt).getTime() : 0), 0)}`,
                unread: t.docs.filter(d => !d.fromAdmin && !d.isRead).length,
                answered: last?.mine === true,
            };
        }).sort((a, b) => new Date(b.last.at) - new Date(a.last.at));
    }, [messages]);

    const visible = threads.filter(t => filter === 'all' || (filter === 'unread' ? t.unread > 0 : t.answered));
    const active = threads.find(t => t.key === activeKey) || null;

    // The open conversation is loaded 20 messages at a time, scrolling up or down for more.
    const chat = useChatWindow({
        fetchPage: params => API.get('/admin/messages/thread', { params: { email: active?.thread, ...params } }).then(r => r.data),
        chatKey: active?.thread || null,
        version: active?.version,
    });

    // ── Message actions: reply, react, edit, delete, star, pin, forward ──
    const [replyTarget, setReplyTarget] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [forwardItem, setForwardItem] = useState(null);
    const [savedKind, setSavedKind] = useState(null);

    const performAction = async (item, action, payload = {}) => {
        try {
            const res = await API.post(`/admin/messages/item/${item.id}/action`, { action, ...payload });
            if (res.data.removed) chat.removeItem(item.id);
            else chat.patchItem(item.id, res.data.item);
            if (action === 'delete' && replyTarget?.id === item.id) setReplyTarget(null);
            return true;
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not update the message');
            return false;
        }
    };

    const menu = useMessageMenu({
        onAction: performAction,
        onReply: item => { setEditTarget(null); setReplyTarget(item); },
        onEdit: item => { setReplyTarget(null); setEditTarget(item); },
        onForward: setForwardItem,
        isOwn: item => item.mine,
        canForward: item => item.mine && !item.legacy, // customers' own messages stay inside their conversation
        attachmentBase: SERVER_URL,
    });

    const authorOf = (from) => (from === 'admin' ? 'You' : active?.name || 'Customer');

    const forwardTo = async (target) => {
        const item = forwardItem;
        setForwardItem(null);
        try {
            await API.post('/admin/messages/send', { email: target.id, content: item.text, attachment: item.attachment, forwarded: true });
            toast.success(`Forwarded to ${target.name}`);
            fetchMessages();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not forward');
        }
    };

    const fetchSaved = async (kind) => {
        const res = await API.get('/admin/messages/thread', { params: { email: active.thread, filter: kind } });
        return res.data.items || [];
    };

    const openThread = async thread => {
        setActiveKey(thread.key);
        setReplyTarget(null);
        setEditTarget(null);
        const unreadDocs = thread.docs.filter(d => !d.fromAdmin && !d.isRead);
        if (!unreadDocs.length) return;
        try {
            await Promise.all(unreadDocs.map(d => API.put(`/admin/messages/${d._id}/read`)));
            window.dispatchEvent(new Event('messagesSeen')); // refresh the sidebar badge
            fetchMessages();
        } catch (err) { }
    };

    // Sends the admin's message (text and / or an uploaded file) into the open conversation.
    const sendReply = async ({ text, attachment, editing }) => {
        if (!active) return false;
        if (editing) return performAction(editing, 'edit', { text });
        try {
            await API.post('/admin/messages/send', { email: active.thread, content: text, attachment, replyToId: replyTarget?.id });
            setReplyTarget(null);
            await fetchMessages();
            chat.jumpToLatest();
            return true;
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to send');
            return false;
        }
    };

    const confirmDeleteThread = async () => {
        const email = pendingDelete;
        if (!email) return;
        setPendingDelete(null);
        try {
            await API.post('/admin/messages/delete-thread', { email });
            toast.success('Conversation deleted!');
            setActiveKey(null);
            fetchMessages();
        } catch (err) {
            toast.error('Failed!');
        }
    };

    const unreadCount = threads.filter(t => t.unread > 0).length;
    const repliedCount = threads.filter(t => t.answered).length;
    let lastDay = null;

    return (
        <div className="messages-layout">
            <Sidebar />
            <div className="admin-content messages-content">
                <Navbar title={<span className="messages-nav-title"><MessageSquare size={18} /> Contact messages</span>} />

                <main className="messages-page">
                    <header className="messages-hero">
                        <div className="messages-hero__copy">
                            <span className="messages-eyebrow"><Sparkles size={14} /> Customer enquiries</span>
                            <h1>Contact messages</h1>
                            <p>Chat with members, service providers and visitors who write through the contact form. Replies reach their Messages inbox instantly.</p>
                        </div>
                        <div className="messages-hero__stats">
                            <div><Inbox size={16} /><strong>{threads.length}</strong><span>Conversations</span></div>
                            <div><Mail size={16} /><strong>{unreadCount}</strong><span>Unread</span></div>
                            <div><CheckCheck size={16} /><strong>{repliedCount}</strong><span>Replied</span></div>
                        </div>
                    </header>

                    <section className="messages-toolbar">
                        <div>
                            <h2>Inbox</h2>
                            <p>{visible.length} {visible.length === 1 ? 'conversation' : 'conversations'} shown</p>
                        </div>
                        <div className="messages-filters" role="group" aria-label="Filter conversations">
                            {FILTERS.map(({ id, label, Icon }) => (
                                <button key={id} type="button" className={filter === id ? 'is-active' : ''} onClick={() => setFilter(id)} aria-pressed={filter === id}>
                                    <Icon size={14} /> {label}
                                </button>
                            ))}
                        </div>
                    </section>

                    <div className={`messages-grid${active ? ' is-chat-open' : ''}`}>
                        <div className="messages-list">
                            {visible.map(t => {
                                const identity = senderIdentity(t);
                                return (
                                <button
                                    type="button"
                                    key={t.key}
                                    className={`message-item${activeKey === t.key ? ' is-selected' : ''}`}
                                    onClick={() => openThread(t)}
                                >
                                    <span className="message-item__avatar">{initial(t.name)}</span>
                                    <span className="message-item__info">
                                        <span className="message-item__top">
                                            <span className="message-item__name">{t.name}</span>
                                            <span className="message-item__time">{formatTime(t.last.at)}</span>
                                        </span>
                                        <span className={`sender-identity is-${identity.kind}`}>
                                            {identity.label}{identity.publicId && <strong>{identity.publicId}</strong>}
                                        </span>
                                        <span className="message-item__preview">{t.last.mine ? 'You: ' : ''}{previewOf(t.last.text, t.last.attachment)}</span>
                                    </span>
                                    {t.unread > 0 && <span className="message-item__badge">{t.unread}</span>}
                                </button>
                                );
                            })}
                            {visible.length === 0 && (
                                <div className="messages-empty">
                                    <span><MessagesSquare size={26} /></span>
                                    <p>No messages</p>
                                </div>
                            )}
                        </div>

                        {active ? (() => {
                            const identity = senderIdentity(active);
                            return (
                            <div className="messages-chat">
                                <div className="chat-header">
                                    <button type="button" className="chat-back" onClick={() => setActiveKey(null)} aria-label="Back to conversations"><ArrowLeft size={18} /></button>
                                    <span className="chat-header__avatar">{initial(active.name)}</span>
                                    <div className="chat-header__info">
                                        <div className="chat-header__title">
                                            <div className="chat-header__name">{active.name}</div>
                                            <span className={`sender-identity is-${identity.kind}`}>
                                                {identity.label}{identity.publicId && <strong>{identity.publicId}</strong>}
                                            </span>
                                        </div>
                                        <div className="chat-header__contact">
                                            <span><Mail size={12} /> {active.email || active.mobile || 'No email'}</span>
                                            {active.mobile && <span><Phone size={12} /> {active.mobile}</span>}
                                        </div>
                                    </div>
                                    <button type="button" className="chat-header__icon" onClick={() => setSavedKind('pinned')} aria-label="Pinned messages" title="Pinned messages"><Pin size={16} /></button>
                                    <button type="button" className="chat-header__icon" onClick={() => setSavedKind('starred')} aria-label="Starred messages" title="Starred messages"><Star size={16} /></button>
                                    <button type="button" className="chat-header__delete" aria-label="Delete conversation" title="Delete conversation" onClick={() => setPendingDelete(active.thread)}>
                                        <Trash2 size={14} /> <span className="chat-header__delete-label">Delete</span>
                                    </button>
                                </div>

                                <PinnedBanner items={chat.items} onOpen={() => setSavedKind('pinned')} />
                                <div className="chat-body" ref={chat.scrollRef} onScroll={chat.onScroll}>
                                    {chat.loadingOlder && <div className="chat-loader"><Loader2 size={14} /> Loading older messages…</div>}
                                    {chat.loading && <div className="chat-loader"><Loader2 size={14} /> Loading messages…</div>}
                                    {chat.items.map(b => {
                                        const day = formatDate(b.at);
                                        const showDay = day !== lastDay;
                                        lastDay = day;
                                        return (
                                            <div key={b.id} data-chat-id={b.id}>
                                                {showDay && <div className="chat-day"><span>{day}</span></div>}
                                                <div className={`chat-row${b.mine ? ' is-mine' : ''}`}>
                                                    {!b.mine && <span className="chat-row__avatar">{initial(active.name)}</span>}
                                                    <div className="msg-bubble-wrap" {...menu.bind(b)}>
                                                        <div className={`chat-bubble${b.mine ? ' is-mine' : ''}`}>
                                                            {b.subject && <div className="chat-bubble__subject">{b.subject}</div>}
                                                            <ForwardedTag item={b} />
                                                            <ReplyQuote reply={b.replyTo} author={authorOf(b.replyTo?.from)} />
                                                            {b.deleted ? <DeletedText /> : (
                                                                <>
                                                                    <ChatAttachment attachment={b.attachment} base={SERVER_URL} />
                                                                    {b.text && <p>{b.text}</p>}
                                                                </>
                                                            )}
                                                            <time><MessageFlags item={b} />{formatTime(b.at)}</time>
                                                        </div>
                                                            <Reactions reactions={b.reactions} onToggle={emoji => performAction(b, 'react', { emoji })} end={b.mine} />
                                                        {!b.legacy && <button type="button" className="msg-menu-btn" onClick={e => menu.openFrom(b, e)} aria-label="Message options"><ChevronDown size={14} /></button>}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {chat.loadingNewer && <div className="chat-loader"><Loader2 size={14} /> Loading newer messages…</div>}
                                </div>

                                <ChatComposer
                                    key={active.key}
                                    placeholder={`Message ${active.name}...`}
                                    onSend={sendReply}
                                    replyTo={replyTarget}
                                    replyLabel={replyTarget ? authorOf(replyTarget.from) : ''}
                                    onCancelReply={() => setReplyTarget(null)}
                                    editing={editTarget}
                                    onCancelEdit={() => setEditTarget(null)}
                                    uploadFile={uploadChatFile}
                                    onError={message => toast.error(message)}
                                    fileBase={SERVER_URL}
                                />
                            </div>
                            );
                        })() : (
                            <div className="messages-placeholder">
                                <span><MessageSquare size={26} /></span>
                                <p>Select a conversation to view</p>
                            </div>
                        )}
                    </div>
                </main>
            </div>

            {menu.element}
            {forwardItem && (
                <ForwardDialog
                    targets={threads.filter(t => t.key !== activeKey).map(t => ({ id: t.thread, name: t.name, subtitle: t.email || t.mobile }))}
                    onPick={forwardTo}
                    onClose={() => setForwardItem(null)}
                />
            )}
            {savedKind && (
                <SavedMessagesDialog
                    kind={savedKind}
                    fetchItems={fetchSaved}
                    labelFor={item => authorOf(item.from)}
                    onRemove={item => performAction(item, savedKind === 'starred' ? 'star' : 'pin')}
                    onClose={() => setSavedKind(null)}
                />
            )}

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title="Delete conversation?"
                message="Every message in this conversation, including your replies, will be permanently removed."
                confirmLabel="Delete conversation"
                danger
                onConfirm={confirmDeleteThread}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
};

export default Messages;
